import './iframe_helper.scss';
import { EventBus } from 'src/event_bus';
import { MESSAGES } from 'constants/communication';
import { ORIENTATION } from 'constants/context';
import { APPLICATION_EVENT, PLAYER_EVENT } from 'constants/events';
import { sendToIframe } from 'utilities/communication';
import { applyStyles } from 'utilities/dom';
import { safeJSON } from 'utilities/parsers';
import { injectSEO } from 'utilities/seo';
import { OrientationContextWatcher } from 'engine/state/context_watcher';

export class IFrameHelper extends EventBus {
    constructor({ embedEl, id, manageSize = true, ...options }) {
        super();
        this.id = id;
        this.embedEl = embedEl;
        this.options = options;
        this.options.manageSize = manageSize;
        this.lastDimension = '';
        this.lastBGColor = '';
        this.pendingTimeout = null;
        this.showingPreview = false;
        this.hiddenPreviewDimensions = {};
        this.orientationChangeWatcher = new OrientationContextWatcher();
        this.orientationChangeWatcher.onChange(this.onOrientationChange);

        window.addEventListener('message', this.onMessage);

        sendToIframe(embedEl.contentWindow, { event: MESSAGES.RECEIVER_AVAILABLE, id });
        sendToIframe(embedEl.contentWindow, { event: MESSAGES.SET_STATE, id, state: ORIENTATION, value: this.orientationChangeWatcher.state });
    }

    onMessage = event => {
        if (!event.data.includes || !event.data.includes(this.id)) {
            return;
        }

        const msg = safeJSON(event.data);

        if (msg.context === 'hapyak' && msg.id && msg.id === this.id) {
            const { args, event, overrides } = msg;

            switch(event) {
                case MESSAGES.OVERRIDES_AVAILABLE:
                    this.loadSEO(overrides);
                    this.emit(APPLICATION_EVENT.SOURCE_LOADED, { loadedSrc: overrides });
                    
                    break;

                case APPLICATION_EVENT.RENDER:
                    if (args && args.regionDimensions && !this.showingPreview) {
                        this.onApplicationRender(args.regionDimensions);
                    }
                    
                    break;

                case MESSAGES.PREVIEW_SHOWN:
                    this.showingPreview = true;
                    if (args && args.previewRect) {
                        this.hiddenPreviewDimensions = this.lastDimension;
                        const { width, height } = args.previewRect;
                        this.changeSize({ container: height / width * 100 }, 0);
                    }
                    break;

                case MESSAGES.PREVIEW_HIDDEN:
                    this.showingPreview = false;
                    this.changeSize({ container: this.hiddenPreviewDimensions }, 0);
                    this.emit(event, args);

                    break;

                case PLAYER_EVENT.FULLSCREEN_CHANGE:
                    if (args && args.supportsAPI === false) {
                        if (args.inFullscreen === true) {
                            this.lastBGColor = document.body.style.backgroundColor;
                            document.body.style.backgroundColor = 'black';
                            this.embedEl.classList.add('fake-fullscreen');
                        } else if (args.inFullscreen === false) {
                            document.body.style.backgroundColor = this.lastBGColor || '';
                            this.embedEl.classList.remove('fake-fullscreen');
                        }
                    }

                    break;

                case APPLICATION_EVENT.UPDATED:
                case APPLICATION_EVENT.STATE_CHANGE:
                case APPLICATION_EVENT.LOAD:
                    this.emit(event, args);
                    break;

                default:
                    if (this.listeners(event)) {
                        this.emit(event, args);
                    }
            }
        }
    }

    onOrientationChange = event => {
        if (this.id && this.embedEl) {
            sendToIframe(
                this.embedEl.contentWindow, {
                    id: this.id,
                    event: MESSAGES.SET_STATE,
                    state: ORIENTATION, 
                    value: this.orientationChangeWatcher.state
                }
            );
        }
    }

    onApplicationRender = regionDimensions => {
        if (regionDimensions.container !== this.lastDimension) {
            this.changeSize(regionDimensions, 60);
            this.lastDimension = regionDimensions.container;
        }
    }

    changeSize(dimensions, timeout = 250) {
        if (!this.options.manageSize || !dimensions.container) {
            return;
        }

        if (this.pendingTimeout) {
            clearTimeout(this.pendingTimeout);
            this.pendingTimeout = null;
        }

        // Wrap execution in setTimeout to prevent video js from intepreting control bar clicks as video mouse downs
        this.pendingTimeout = setTimeout(() => {
            let aspect = dimensions.container;
            const { width: fixedWidth } = this.fixedDimensions;

            // if container has fixed pixel width, compute pixel height as according to aspect ratio
            if (this.hasFixedDimension && fixedWidth.endsWith('px')) {
                const width = Number(fixedWidth.replace('px',''));
                aspect = aspect / 100 * width;
                this.aspectContainer.style.height = `${aspect}px`;
                this.aspectContainer.style.paddingTop = ''; // dimensions are "fixed", so don't set padding top
            } 

            // if container has fixed percent width, compute padding top proportional to aspect ratio of width
            else if (this.hasFixedDimension && fixedWidth.endsWith('%')) {
                const width = Number(fixedWidth.replace('%',''));
                aspect = aspect / 100 * width;
                this.aspectContainer.style.paddingTop = `${aspect}%`;
            }

            // otherwise set aspect ratio as padding top
            else {
                this.aspectContainer.style.width = '';
                this.aspectContainer.style.margin = '';
                this.aspectContainer.style.left = '';
                this.aspectContainer.style.right = '';
                this.aspectContainer.style.paddingTop = `${aspect}%`;
            }
            
            this.pendingTimeout = null;
        }, timeout);
    }

    loadSEO(overrides = {}) {
        if (overrides.project && overrides.project.seo && overrides.project.seo.enabled) {
            injectSEO(overrides.project)
                .then(() => this.emit(APPLICATION_EVENT.SEO_INJECTED));
        }
    }

    showPreview() {
        sendToIframe(this.embedEl.contentWindow, { event: MESSAGES.SHOW_PREVIEW, id: this.id });
    }

    destroy() {
        this.orientationChangeWatcher.destroy();
        window.removeEventListener('message', this.onMessage);
    }

    get aspectContainer() {
        if (this.embedEl && this.embedEl.parentElement && this.embedEl.parentElement.classList.contains('hapyak-player-embed')) {
            return this.embedEl.parentElement;
        }

        return document.createElement('div'); // provide dummy element if parent container not found
    }

    get windowDimensions() {
        return {
            width: window.innerWidth,
            height: window.innerHeight
        }
    }

    get fixedDimensions() {
        const dimensions = {};

        if (this.aspectContainer && this.aspectContainer.style) {
            const { width } = this.aspectContainer.style;

            if (width.endsWith('px') || width.endsWith('%')) {
                dimensions.width = width;
            }
        }

        return dimensions;
    }

    get hasFixedDimension() {
        const { fixedWidth } = this.aspectContainer.dataset;
        return (
            fixedWidth &&
                (
                    fixedWidth === 'true' ||
                    fixedWidth.endsWith('px') ||
                    fixedWidth.endsWith('%')
                )
        );
    }
}
